import React from 'react';
import { Box, Button, Typography } from '@mui/material';
import { Translate } from 'react-localize-redux';
import { DecryptFailureStage } from '../../hooks/useDecryptedFiles';

interface UnavailableImageProps {
    failedAt?: DecryptFailureStage;
    /** The underlying error, shown beneath the frame where it is free to wrap. */
    error?: string;
    /** Re-run retrieval + decryption. Omitted when the caller has nothing to re-run. */
    onRetry?: () => void;
    /** Edge of the square frame, matching the thumbnail slot it stands in. */
    size?: number;
}

/**
 * SKIN-206 — the visible failure for a capture the viewer cannot show. Occupies the same
 * slot as a thumbnail so the rest of the visit still reads as a gallery, and names the
 * reason rather than leaving a silent gap.
 *
 * The headline distinguishes the two failures, because they are different claims about the
 * patient's data: "could not be decoded" says the bytes we hold are unreadable, which for a
 * clinical image is worth being accurate about. A presigned-URL or decrypt failure says
 * nothing about the bytes and must not be reported as corruption.
 *
 * SKIN-240 — extracted from `EncryptedImage.tsx` and given a retry, because the QC review
 * gallery renders the same failure and must offer the reviewer a way out of it (the usual
 * cause is an expired presigned URL or one request that failed, so a retry fixes it far
 * more often than a page reload). It lives in its own module rather than staying an export
 * of `EncryptedImage.tsx` so that the QC page imports a shared component instead of
 * reaching into another component's file for its internals.
 *
 * Its copy is keyed under `general.image.*`, not under any feature. The clinical
 * submission view renders this same component, and a general component drawing its strings
 * from a feature's namespace means deleting that feature would silently blank a screen
 * that has nothing to do with it.
 *
 * **The error text sits outside the square, not inside it.** It is the one part of this
 * component with no bound on its length, so putting it in a fixed `size`x`size` frame
 * clips it — which is worse than useless, because the whole point of showing the error is
 * that a reviewer can tell an expired URL from a real problem. The frame stays square so
 * the gallery keeps its grid; only the caption grows.
 */
export default function UnavailableImage({ failedAt, error, onRetry, size = 160 }: UnavailableImageProps) {
    return (
        <Box
            data-testid="image-unavailable"
            data-failed-at={failedAt}
            sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: size }}
        >
            <Box
                sx={{
                    width: size,
                    height: size,
                    flexShrink: 0,
                    borderRadius: 1,
                    border: '1px dashed',
                    borderColor: 'error.main',
                    bgcolor: 'grey.50',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 0.5,
                    p: 1,
                }}
            >
                <Typography variant="caption" color="error" align="center" data-testid="unavailable-image">
                    <Translate
                        id={failedAt === 'decode' ? 'general.image.decode-failed' : 'general.image.retrieve-failed'}
                    />
                </Typography>
                {onRetry && (
                    <Button size="small" onClick={onRetry} data-testid="retry-decrypt">
                        <Translate id="general.retry" />
                    </Button>
                )}
            </Box>
            {error && (
                <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                    {error}
                </Typography>
            )}
        </Box>
    );
}
